import { PeakLevel, chooseLevels, encodePeak } from "./PeakData.js";
import { WaveformSource } from "./WaveformSource.js";

/**
 * Peaks generated from a decoded AudioBuffer, for locally-loaded files.
 *
 * Everything is resident, so `request()` stays a no-op and `getView()` always
 * returns the ideal level.
 */
export class LocalWaveform extends WaveformSource {
  constructor(audioBuffer) {
    super();
    for (const level of WaveformGenerator.generateLevels(audioBuffer)) {
      this.addLevel(level);
    }
  }
}

export class WaveformGenerator {
  /**
   * Build every mipmap level for a buffer.
   *
   * The finest level is computed in a single pass over the samples and the
   * coarser ones are decimated from it — min of mins, max of maxes. Because
   * rounding to Int16 is monotonic, that's bit-identical to computing each level
   * from the samples directly, at a fraction of the cost. The old
   * implementation walked the whole buffer once per level.
   *
   * @param {AudioBuffer} audioBuffer
   * @returns {PeakLevel[]} ascending by samplesPerPeak
   */
  static generateLevels(audioBuffer) {
    const samples = audioBuffer.getChannelData(0);
    const sizes = chooseLevels(samples.length);

    const finest = this.calculatePeaks(samples, sizes[0]);
    const levels = [finest];

    for (let i = 1; i < sizes.length; i++) {
      levels.push(this.decimate(levels[i - 1], sizes[i], samples.length));
    }

    return levels;
  }

  /**
   * @param {Float32Array} samples
   * @param {number} samplesPerPeak
   * @returns {PeakLevel}
   */
  static calculatePeaks(samples, samplesPerPeak) {
    const count = Math.ceil(samples.length / samplesPerPeak);
    const data = new Int16Array(count * 2);

    for (let peak = 0; peak < count; peak++) {
      const from = peak * samplesPerPeak;
      const to = Math.min(from + samplesPerPeak, samples.length);

      let min = Infinity;
      let max = -Infinity;
      for (let i = from; i < to; i++) {
        const value = samples[i];
        if (value < min) min = value;
        if (value > max) max = value;
      }

      data[peak * 2] = encodePeak(min);
      data[peak * 2 + 1] = encodePeak(max);
    }

    const level = new PeakLevel(samplesPerPeak, count);
    level.setBlock(0, data);
    return level;
  }

  /** Build a coarser level by reducing a finer one. */
  static decimate(finer, samplesPerPeak, sampleCount) {
    const ratio = Math.round(samplesPerPeak / finer.samplesPerPeak);
    const count = Math.ceil(sampleCount / samplesPerPeak);
    const source = finer.blocks.get(0);
    const data = new Int16Array(count * 2);

    for (let peak = 0; peak < count; peak++) {
      const from = peak * ratio;
      const to = Math.min(from + ratio, finer.count);

      let min = 32767;
      let max = -32768;
      for (let i = from; i < to; i++) {
        if (source[i * 2] < min) min = source[i * 2];
        if (source[i * 2 + 1] > max) max = source[i * 2 + 1];
      }

      const empty = min === 32767 && max === -32768;
      data[peak * 2] = empty ? 0 : min;
      data[peak * 2 + 1] = empty ? 0 : max;
    }

    const level = new PeakLevel(samplesPerPeak, count);
    level.setBlock(0, data);
    return level;
  }
}
